// client/src/components/campaign/CampaignList.jsx
//
// All marketing campaigns for a tenant (On-Screen 2.0). Each row links to
// the campaign detail / content pipeline.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaHome, FaPlus } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import { getSessionLocal } from '../../services/sessionLocal';
import { can } from '../../services/rolePermissions';
import { listCampaigns, CAMPAIGN_STATUSES } from '../../services/campaignsApi';
import CampaignForm from './CampaignForm';

const STATUS_SURFACE = {
  planning: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200',
  paused: 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200',
  completed: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200',
  archived: 'bg-black/10 text-black/50 dark:bg-white/10 dark:text-white/50',
};

export default function CampaignList({ tenantId: tenantProp }) {
  const params = useParams();
  const tenantId = tenantProp || params.tenant;
  const navigate = useNavigate();
  const { role } = getSessionLocal();
  const canCreate = can(role, 'events', 'add');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCampaigns(tenantId, statusFilter ? { status: statusFilter } : {});
      setRows(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [tenantId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const footer = (
    <AppFooter buttons={[{ icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenantId}/dashboard`) }]} />
  );

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4" style={{ paddingBottom: 72 }}>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Campaigns</h1>
        {canCreate && (
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 rounded bg-cobalt px-3 py-1.5 text-sm text-white"
          >
            <FaPlus size={12} /> New campaign
          </button>
        )}
      </div>

      <div className="flex gap-2 text-sm">
        <button
          onClick={() => setStatusFilter('')}
          className={`rounded-full px-3 py-1 ${statusFilter === '' ? 'bg-cobalt text-white' : 'bg-black/5 dark:bg-white/10'}`}
        >
          All
        </button>
        {CAMPAIGN_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`rounded-full px-3 py-1 capitalize ${statusFilter === s ? 'bg-cobalt text-white' : 'bg-black/5 dark:bg-white/10'}`}
          >
            {s}
          </button>
        ))}
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading ? (
        <div className="text-sm opacity-70">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-black/10 p-6 text-center text-sm opacity-60 dark:border-white/10">
          No campaigns yet. Create an event and its marketing campaign is set up automatically.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/${tenantId}/campaigns/${c.id}`)}
              className="block w-full rounded-lg border border-black/10 p-3 text-left hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/5"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{c.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_SURFACE[c.status] || ''}`}>
                  {c.status}
                </span>
              </div>
              <div className="mt-1 text-xs opacity-60">
                <span className="capitalize">{c.objective}</span>
                {c.eventName ? ` · ${c.eventName}` : ''}
                {` · ${c.postCount} post${c.postCount === 1 ? '' : 's'}`}
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && (
        <CampaignForm
          tenantId={tenantId}
          onClose={() => setCreating(false)}
          onSaved={(c) => { setCreating(false); if (c?.id) navigate(`/${tenantId}/campaigns/${c.id}`); else load(); }}
        />
      )}

      {footer}
    </div>
  );
}
